/* eslint-disable max-lines */
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type {
  BidReviewResult,
  CvMatchResult,
  SourceRef,
  SourceSnippet,
  TenderCase,
  TenderDocument,
} from "../types"
import type {
  ActivityMessage,
  ChatAppRequest,
  ChatAppResponse,
  ChatSessionModel,
  ResponseContext,
  ServerMessage,
} from "@/api/models"
import type { TenderExtractionApi } from "@/api/tender-api"

import { explorerChatWebSocketApi } from "@/api/api"
import { VectorFieldOptions } from "@/api/models"
import { getTenderDocumentUrl } from "@/api/tender-api"
import { Check, Clock, Loader2, MoreHorizontal, Pencil, Plus, Trash2, X } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import { ExplorerFooterToolbar } from "@/features/ai-explorer/components/explorer-footer-toolbar"
import { M365InfoModal } from "@/features/ai-explorer/components/m365-info-modal"
import { useM365ApprovalGate } from "@/features/ai-explorer/hooks/use-m365-approval-gate"
import { Chat } from "@/features/chat/components/chat"
import { CitationPeekContent } from "@/features/chat/components/citation-peek-content"
import { CitationPreviewProvider } from "@/features/chat/contexts/citation-preview-context"
import { useLibrariesSelection } from "@/features/navigator/hooks/use-libraries-selection"
import { useAvailableGPTModels } from "@/hooks/use-config-query"
import { useM365Enabled, usePromptsEnabled } from "@/hooks/use-route-overrides"
import { safeImport } from "@/lib/chunk-reload"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"
import { usePreferencesStore } from "@/stores/preferences-store"
import { useTenderChatStore } from "@/stores/tender-chat-store"
import { KNOWN_SESSION_TYPES } from "@/types"
import { translateActivityMessage } from "@/utils/activity-message-translator"
import { parseCitationParts } from "@/utils/citation-utils"

import { tenderUiLocaleTag } from "../utils/tender-ui-locale-utils"
import { ExtractionCitationCard, type OpenSourceFn } from "./sections/extraction-citation"
import {
  buildDocumentNameLookup,
  buildExtractionLookup,
  type ExtractionItem,
  type ExtractionItemLookup,
  normalizeDocumentNameLookupKey,
} from "./sections/extraction-citation-utils"
import { pushTenderCitationPanel } from "./tender-citation-panel"
import {
  TENDER_CHAT_SESSION_KEYS,
  transformStoredMessages,
  useCreateTenderChatSession,
  useDeleteTenderChatSession,
  useRenameTenderChatSession,
  useTenderChatMessages,
  useTenderChatSessions,
} from "../hooks/use-tender-chat-sessions"
import { buildTenderMessage } from "../utils/build-tender-message"

// ── Types ────────────────────────────────────────────────────────────────────

type StepIndex = 1 | 2 | 3 | 4 | 5 | 6

const STEP_BADGE_I18N_KEY: Record<StepIndex, string> = {
  1: "tender.steps.setup",
  2: "tender.steps.extraction",
  3: "tender.steps.cvMatch",
  4: "tender.steps.bidReview",
  5: "tender.steps.analysis",
  6: "tender.steps.recommendation",
}

type ChatMessage = {
  id: string
  role: "user" | "assistant"
  content: string
  answer?: ChatAppResponse
}

function buildDocumentNameVariants(value: string): string[] {
  const trimmed = value.trim()
  if (!trimmed) return []

  const decoded = (() => {
    try {
      return decodeURIComponent(trimmed)
    } catch {
      return trimmed
    }
  })()

  const withoutQuery = decoded.split(/[?#]/)[0] ?? decoded
  const basename = withoutQuery.split(/[\\/]/).pop() ?? withoutQuery

  const variants = new Set<string>()
  const addVariant = (candidate: string) => {
    const normalized = normalizeDocumentNameLookupKey(candidate)
    if (!normalized) return
    variants.add(normalized)
  }

  const addWithTrimmedExtensions = (candidate: string) => {
    addVariant(candidate)

    let current = candidate.trim()
    while (current.toLowerCase().endsWith(".md")) {
      current = current.slice(0, -3)
      addVariant(current)
    }

    const withoutExtension = current.replace(/\.[^/.]+$/, "")
    if (withoutExtension !== current) {
      addVariant(withoutExtension)
    }
  }

  addWithTrimmedExtensions(trimmed)
  addWithTrimmedExtensions(withoutQuery)
  addWithTrimmedExtensions(basename)

  return Array.from(variants)
}

// ── Tender citation hover peek ────────────────────────────────────────────────

const PEEK_OPEN_DELAY_MS = 350
const PEEK_CLOSE_DELAY_MS = 150
const PEEK_WIDTH_PX = 320
const EXTRACTION_PEEK_WIDTH_PX = 380
const PEEK_OFFSET_PX = 8
const PEEK_MAX_HEIGHT_PX = 260
const EXTRACTION_PEEK_MAX_HEIGHT_PX = 380
const PEEK_EDGE_MARGIN_PX = 12

type DocumentPeekState = {
  kind: "document"
  title: string
  pageNumber: number | null
  quoteText: string | null
  anchorRect: DOMRect
  onOpen: () => void
}

type ExtractionPeekState = {
  kind: "extraction"
  item: ExtractionItem
  anchorRect: DOMRect
}

type PeekState = DocumentPeekState | ExtractionPeekState

type TenderCitationHoverPeekProps = {
  containerRef: React.RefObject<HTMLDivElement | null>
  documents: TenderDocument[]
  caseId: string
  extractionLookup: ExtractionItemLookup
  onOpenExtractionSource: (source: SourceRef | SourceSnippet) => boolean
}

function TenderCitationHoverPeek({
  containerRef,
  documents,
  caseId,
  extractionLookup,
  onOpenExtractionSource,
}: TenderCitationHoverPeekProps) {
  const [peekState, setPeekState] = useState<PeekState | null>(null)
  const cardRef = useRef<HTMLDivElement | null>(null)
  const hoveredRef = useRef<HTMLButtonElement | null>(null)
  const openTimerRef = useRef<number | null>(null)
  const closeTimerRef = useRef<number | null>(null)

  const clearOpen = useCallback(() => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }
  }, [])

  const clearClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const close = useCallback(() => {
    clearOpen()
    clearClose()
    hoveredRef.current = null
    setPeekState(null)
  }, [clearClose, clearOpen])

  const scheduleClose = useCallback(() => {
    clearClose()
    closeTimerRef.current = window.setTimeout(close, PEEK_CLOSE_DELAY_MS)
  }, [clearClose, close])

  const handleOpenExtractionSource = useCallback<OpenSourceFn>(
    (source) => {
      const didOpen = onOpenExtractionSource(source)
      if (didOpen) {
        close()
      }
    },
    [close, onOpenExtractionSource]
  )

  useEffect(() => {
    const root = containerRef.current
    if (!root) return

    const handleMouseOver = (e: MouseEvent) => {
      const btn = (e.target as Element).closest("button.supContainer")
      if (!(btn instanceof HTMLButtonElement) || !root.contains(btn)) return
      if (hoveredRef.current === btn && peekState) return

      hoveredRef.current = btn
      clearClose()
      clearOpen()

      const raw = btn.dataset.citationRaw
      if (!raw) return

      const parsed = parseCitationParts(raw)

      // Handle extraction references (req:ID)
      if (parsed.source.startsWith("req:")) {
        const reqId = parsed.source.slice(4)
        const item = extractionLookup.get(reqId)
        if (!item) return
        const anchorRect = btn.getBoundingClientRect()
        openTimerRef.current = window.setTimeout(() => {
          if (hoveredRef.current !== btn) return
          setPeekState({
            kind: "extraction",
            item,
            anchorRect,
          })
        }, PEEK_OPEN_DELAY_MS)
        return
      }

      const docRef = parsed.source.startsWith("doc:") ? parsed.source.slice(4) : parsed.source
      const refLower = docRef.toLowerCase()
      const doc =
        documents.find((d) => d.id === docRef) ??
        documents.find((d) => d.filename.toLowerCase() === refLower)
      if (!doc) return

      const anchorRect = btn.getBoundingClientRect()
      const onOpen = () => {
        const documentUrl = getTenderDocumentUrl({
          caseId,
          docId: doc.id,
          filename: doc.filename,
          page: parsed.page ?? undefined,
        })
        pushTenderCitationPanel({
          title: doc.filename,
          documentUrl,
          quoteSnippet: parsed.quote ?? undefined,
        })
        close()
      }

      openTimerRef.current = window.setTimeout(() => {
        if (hoveredRef.current !== btn) return
        setPeekState({
          kind: "document",
          title: doc.filename,
          pageNumber: parsed.page ?? null,
          quoteText: parsed.quote ?? null,
          anchorRect,
          onOpen,
        })
      }, PEEK_OPEN_DELAY_MS)
    }

    const handleMouseOut = (e: MouseEvent) => {
      const related = e.relatedTarget
      if (related instanceof Node && cardRef.current?.contains(related)) return
      if (related instanceof Element && root.contains(related.closest("button.supContainer")))
        return
      scheduleClose()
    }

    root.addEventListener("mouseover", handleMouseOver)
    root.addEventListener("mouseout", handleMouseOut)
    return () => {
      root.removeEventListener("mouseover", handleMouseOver)
      root.removeEventListener("mouseout", handleMouseOut)
      clearOpen()
      clearClose()
    }
  }, [
    containerRef,
    documents,
    caseId,
    extractionLookup,
    peekState,
    clearOpen,
    clearClose,
    scheduleClose,
    close,
  ])

  if (!peekState) return null

  const { anchorRect } = peekState
  const peekWidthPx = peekState.kind === "extraction" ? EXTRACTION_PEEK_WIDTH_PX : PEEK_WIDTH_PX
  const peekMaxHeightPx =
    peekState.kind === "extraction" ? EXTRACTION_PEEK_MAX_HEIGHT_PX : PEEK_MAX_HEIGHT_PX
  const availableBelow =
    window.innerHeight - anchorRect.bottom - PEEK_OFFSET_PX - PEEK_EDGE_MARGIN_PX
  const availableAbove = anchorRect.top - PEEK_OFFSET_PX - PEEK_EDGE_MARGIN_PX
  const placement = availableBelow >= availableAbove ? "below" : "above"
  const maxHeight = Math.max(
    0,
    Math.min(peekMaxHeightPx, placement === "below" ? availableBelow : availableAbove)
  )
  const maxLeft = Math.max(
    PEEK_EDGE_MARGIN_PX,
    window.innerWidth - peekWidthPx - PEEK_EDGE_MARGIN_PX
  )
  const left = Math.min(Math.max(anchorRect.left, PEEK_EDGE_MARGIN_PX), maxLeft)

  const style: CSSProperties = {
    position: "fixed",
    left: `${left}px`,
    width: `${peekWidthPx}px`,
    maxHeight: `${maxHeight}px`,
    overflowY: "auto",
    zIndex: 60,
    ...(placement === "below"
      ? { top: `${anchorRect.bottom + PEEK_OFFSET_PX}px` }
      : { bottom: `${window.innerHeight - anchorRect.top + PEEK_OFFSET_PX}px` }),
  }

  return createPortal(
    <div
      ref={cardRef}
      role="presentation"
      style={style}
      className={
        peekState.kind === "extraction"
          ? "border-border/60 bg-background/80 rounded-md border p-4 shadow-xl backdrop-blur-md"
          : "bg-popover text-popover-foreground cursor-pointer rounded-md border p-4 shadow-md"
      }
      onMouseEnter={clearClose}
      onMouseLeave={scheduleClose}
      onClick={
        peekState.kind === "document"
          ? (e) => {
              // The native "view in document" button inside is the keyboard route and
              // opens the document itself; the rest of the card is a mouse shortcut.
              if ((e.target as HTMLElement).closest("button")) return
              peekState.onOpen()
            }
          : undefined
      }
    >
      {peekState.kind === "extraction" ? (
        <ExtractionCitationCard item={peekState.item} onOpenSource={handleOpenExtractionSource} />
      ) : (
        <CitationPeekContent
          interactionMode="button"
          pageNumber={peekState.pageNumber}
          quoteText={peekState.quoteText}
          title={peekState.title}
          onViewDocument={peekState.onOpen}
        />
      )}
    </div>,
    document.body
  )
}

// ── Minimal ChatAppResponse factory ──────────────────────────────────────────

const EMPTY_CONTEXT: ResponseContext = {
  data_points: { text: [] },
  followup_questions: null,
  thoughts: [],
}

function makeAnswer(content: string): ChatAppResponse {
  return { content, context: EMPTY_CONTEXT, session_state: null, completed: true }
}

// ── Component props ───────────────────────────────────────────────────────────

type TenderChatPanelProps = {
  tenderCase: TenderCase
  activeStep: StepIndex
  extractionData?: TenderExtractionApi
  bidReviewData?: BidReviewResult
  cvMatchData?: CvMatchResult
  documents?: TenderDocument[]
  runId?: string
}

export function TenderChatPanel({
  tenderCase,
  activeStep,
  extractionData,
  bidReviewData,
  cvMatchData,
  documents = [],
  runId,
}: TenderChatPanelProps) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const store = useTenderChatStore(tenderCase.id)()

  const { activeLibraryIds } = useLibrariesSelection("tender")

  const isPromptsFeatureEnabled = usePromptsEnabled()
  const showM365Option = useM365Enabled()
  const showWebSearchOption =
    SettingsService.getInstance().settings.enabled_tools?.includes("web_search") ?? false
  const { models: availableModels } = useAvailableGPTModels("tender")

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [activityMessage, setActivityMessage] = useState("")
  const [enableWebSearch, setEnableWebSearch] = useState(false)
  const [enableM365, setEnableM365] = useState(false)
  const [showM365Info, setShowM365Info] = useState(false)
  const [enableHighReasoning, setEnableHighReasoning] = useState(false)
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null)
  const [currentSessionRunId, setCurrentSessionRunId] = useState<string | null>(null)
  const [isHydrating, setIsHydrating] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [renamingSessionId, setRenamingSessionId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState("")

  useEffect(() => {
    if (renamingSessionId) {
      renameInputRef.current?.focus()
    }
  }, [renamingSessionId])

  const initialLoadDone = useRef(false)
  // Set to true when we lazily create a new session mid-send so the hydration
  // effect doesn't wipe the optimistic/streaming messages with the empty fetch result.
  const sessionCreatedLocally = useRef(false)

  const abortControllerRef = useRef<AbortController | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const messagesRef = useRef<ChatMessage[]>([])
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const renameInputRef = useRef<HTMLInputElement>(null)
  const setQuestionRef = useRef<((text: string) => void) | null>(null)

  const handleSetQuestion = useCallback((setter: (text: string) => void) => {
    setQuestionRef.current = setter
  }, [])

  const isStreaming = store.isStreaming
  const setIsStreaming = store.setIsStreaming
  const setAbortStreaming = store.setAbortStreaming

  const showHighReasoning = availableModels.some(
    (model) => model.id === "gpt-5_eu" || model.id === "gpt-5.1_eu"
  )
  const showSuggestions = messages.length === 0 && !isHydrating
  const { approveM365, declineM365, hasM365Approval, hasDeclinedM365 } = useM365ApprovalGate()
  const openPreferencesWith = usePreferencesStore((s) => s.openWith)

  const stepSuggestions = useMemo((): string[] => {
    const raw = t(`tender.chat.suggestedByStep.${activeStep}`, { returnObjects: true })
    if (!Array.isArray(raw)) return []
    return raw.filter((item): item is string => typeof item === "string")
  }, [t, activeStep])

  const formatSessionDate = useCallback(
    (dateStr: string | undefined): string => {
      if (!dateStr) return ""
      const d = new Date(dateStr)
      const now = new Date()
      const diffMs = now.getTime() - d.getTime()
      const diffMins = Math.floor(diffMs / 60_000)
      if (diffMins < 1) return t("tender.landing.activity.justNow")
      if (diffMins < 60) return t("tender.landing.activity.minutesAgo", { count: diffMins })
      const diffHours = Math.floor(diffMins / 60)
      if (diffHours < 24) return t("tender.landing.activity.hoursAgo", { count: diffHours })
      const diffDays = Math.floor(diffHours / 24)
      if (diffDays < 7) return t("tender.landing.activity.daysAgo", { count: diffDays })
      return d.toLocaleDateString(tenderUiLocaleTag(i18n.language), {
        day: "numeric",
        month: "short",
      })
    },
    [t, i18n.language]
  )

  const extractionLookup = useMemo(() => buildExtractionLookup(extractionData), [extractionData])
  const documentNameLookup = useMemo(
    () => buildDocumentNameLookup(documents, extractionData),
    [documents, extractionData]
  )

  const openTenderSourcePreview = useCallback(
    (source: SourceRef | SourceSnippet, title = source.documentName) => {
      if (source.kind !== "file") return false

      const nameVariants = buildDocumentNameVariants(source.documentName)

      const docId =
        source.documentId ||
        nameVariants
          .map((variant) => documentNameLookup.get(variant))
          .find((resolvedId): resolvedId is string => Boolean(resolvedId))
      if (!docId) return false

      const documentUrl = getTenderDocumentUrl({
        caseId: tenderCase.id,
        docId,
        filename: source.documentName,
        page: source.page,
      })

      pushTenderCitationPanel({
        title,
        documentUrl,
        quoteSnippet: "quote" in source ? source.quote : undefined,
      })
      return true
    },
    [documentNameLookup, tenderCase.id]
  )

  const handleCitationClick = useCallback(
    (rawCitation: string) => {
      if (rawCitation.startsWith("req:")) {
        const reqId = rawCitation.slice(4)
        const item = extractionLookup.get(reqId)
        let sourceToOpen: SourceRef | SourceSnippet | undefined
        if (item?.snippet?.kind === "file") {
          sourceToOpen = item.snippet
        } else if (item?.source?.kind === "file") {
          sourceToOpen = item.source
        }

        if (sourceToOpen) {
          openTenderSourcePreview(sourceToOpen, item ? `${item.id} — ${item.label}` : undefined)
        }
        return
      }

      const parsed = parseCitationParts(rawCitation)
      const docRef = parsed.source.startsWith("doc:") ? parsed.source.slice(4) : parsed.source
      const refLower = docRef.toLowerCase()
      const doc =
        documents.find((d) => d.id === docRef) ??
        documents.find((d) => d.filename.toLowerCase() === refLower)
      if (!doc) return
      const documentUrl = getTenderDocumentUrl({
        caseId: tenderCase.id,
        docId: doc.id,
        filename: doc.filename,
        page: parsed.page ?? undefined,
      })
      pushTenderCitationPanel({
        title: doc.filename,
        documentUrl,
        quoteSnippet: parsed.quote ?? undefined,
      })
    },
    [documents, extractionLookup, openTenderSourcePreview, tenderCase.id]
  )

  // ── Session hooks ────────────────────────────────────────────────────────
  const { data: sessions = [], isFetched: isSessionsFetched } = useTenderChatSessions(tenderCase.id)
  const createSession = useCreateTenderChatSession()
  const deleteSession = useDeleteTenderChatSession()
  const renameSession = useRenameTenderChatSession()
  const { data: storedMessages, isFetched: isMessagesFetched } = useTenderChatMessages(
    currentSessionId ?? undefined
  )

  // Auto-select the most recent session on first load.
  // Gate on isSessionsFetched (not isMessagesFetched) so users with zero sessions
  // also mark initialLoad as done once the query resolves.
  useEffect(() => {
    if (initialLoadDone.current || !isSessionsFetched) return
    if (sessions.length > 0) {
      const latestId = sessions[0]?.id
      if (latestId) {
        setCurrentSessionId(latestId)
        setIsHydrating(true)
      }
    }
    initialLoadDone.current = true
  }, [sessions, isSessionsFetched])

  // Hydrate messages when a session is loaded from history.
  // Skip when the session was just created locally (lazy creation on first send) —
  // in that case the in-memory messages are the source of truth, not storage.
  useEffect(() => {
    if (!currentSessionId || !isMessagesFetched) return
    if (sessionCreatedLocally.current) return
    if (!storedMessages || storedMessages.length === 0) {
      setMessages([])
      setIsHydrating(false)
      return
    }
    const transformed = transformStoredMessages(storedMessages)
    setMessages(transformed)
    setIsHydrating(false)
  }, [currentSessionId, storedMessages, isMessagesFetched])

  // Auto-reset chat when run changes (req references belong to a specific run)
  const prevRunIdRef = useRef(runId)
  useEffect(() => {
    if (prevRunIdRef.current && runId && prevRunIdRef.current !== runId) {
      sessionCreatedLocally.current = false
      setCurrentSessionId(null)
      setCurrentSessionRunId(null)
      setMessages([])
    }
    prevRunIdRef.current = runId
  }, [runId])

  // Keep ref in sync for stable callbacks
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, isStreaming, activityMessage])

  const handleCancel = useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    setIsStreaming(false)
    setAbortStreaming(null)
    setActivityMessage("")
  }, [setIsStreaming, setAbortStreaming])

  const handleServerMessage = useCallback(
    (message: ServerMessage) => {
      if (message.type === "title_state_changed") {
        void queryClient.invalidateQueries({
          queryKey: TENDER_CHAT_SESSION_KEYS.sessions(tenderCase.id),
        })
        return
      }

      if (message.type !== "activity") return
      const activityMsg = message as ActivityMessage
      const translated = translateActivityMessage(activityMsg, t)
      if (translated) {
        setActivityMessage(translated)
      }
    },
    [t, queryClient, tenderCase.id]
  )

  const handleSend = useCallback(
    async (question: string) => {
      const trimmed = question.trim()
      if (!trimmed || isStreaming) return

      // Capture history BEFORE the optimistic push so the model receives:
      //   [prior turns...] + [new context-wrapped user message]
      // and NOT the bare user message or empty assistant placeholder.
      const priorMessages = messagesRef.current

      const userMsgId = crypto.randomUUID()
      const userMsg: ChatMessage = { id: userMsgId, role: "user", content: trimmed }
      const assistantMsgId = crypto.randomUUID()
      const assistantMsg: ChatMessage = { id: assistantMsgId, role: "assistant", content: "" }

      setMessages((prev) => [...prev, userMsg, assistantMsg])
      setIsStreaming(true)
      setActivityMessage("")

      const abortController = new AbortController()
      abortControllerRef.current = abortController
      setAbortStreaming(() => () => abortController.abort())

      let sessionId = currentSessionId

      // Lazily create session on first message. Roll back optimistic messages
      // if this fails — there is nowhere to persist the conversation without a session.
      if (!sessionId) {
        try {
          const newSession = await createSession.mutateAsync({ caseId: tenderCase.id, runId })
          sessionId = newSession?.id ?? null
          if (!sessionId) throw new Error("No session ID returned")
          sessionCreatedLocally.current = true
          setCurrentSessionId(sessionId)
        } catch {
          setMessages((prev) => prev.filter((m) => m.id !== userMsgId && m.id !== assistantMsgId))
          setIsStreaming(false)
          setAbortStreaming(null)
          toast.error(t("tender.chat.toastCouldNotStart"), {
            description: t("tender.chat.toastTryAgainShort"),
          })
          return
        }
      }

      try {
        const token = SettingsService.getInstance().token

        const historyForModel: ChatAppRequest["messages"] = [
          ...priorMessages.map((m) => ({ role: m.role, content: m.content })),
          {
            role: "user",
            content: buildTenderMessage({
              tenderCase,
              activeStep,
              extractionData,
              bidReviewData,
              cvMatchData,
              userPrompt: trimmed,
            }),
          },
        ]

        const request: ChatAppRequest = {
          messages: historyForModel,
          context: {
            overrides: {
              agent_name: "tender_assistant",
              use_custom_gpt: true,
              custom_gpt_model: "auto",
              vector_fields: [VectorFieldOptions.Embedding],
              tender_case_id: tenderCase.id,
              session_id: sessionId,
              use_web_search: enableWebSearch,
              use_m365: showM365Option && hasM365Approval ? enableM365 : false,
              reasoning_effort: enableHighReasoning ? "high" : "medium",
              search_services: activeLibraryIds,
            },
          },
          stream: true,
          session_state: null,
        }

        const response = await explorerChatWebSocketApi(
          request,
          undefined,
          token,
          abortController.signal,
          handleServerMessage,
          "tender",
          sessionId
        )

        if (!response.body) {
          throw new Error("No response body")
        }

        const { default: readNDJSONStream } = await safeImport(
          () => import("ndjson-readablestream")
        )
        let accumulated = ""
        let lastContext: ResponseContext = EMPTY_CONTEXT

        for await (const event of readNDJSONStream(response.body)) {
          if (!event) continue
          if (abortController.signal.aborted) {
            const err = new Error("AbortError")
            err.name = "AbortError"
            throw err
          }

          if (event?.error || event?.type === "error") {
            throw new Error(String(event?.message ?? event?.error ?? "Unknown error"))
          }

          if (event.context) {
            lastContext = event.context as ResponseContext
          }

          if (typeof event.content === "string" && event.content.length > 0) {
            accumulated += event.content
            const streamingAnswer: ChatAppResponse = {
              content: accumulated,
              context: lastContext,
              session_state: null,
              completed: false,
            }
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? { ...m, content: accumulated, answer: streamingAnswer }
                  : m
              )
            )
          }

          // Early-exit on the explicit completion marker. The backend keeps
          // the WebSocket open after the last token while it persists the
          // assistant message to Cosmos DB; without this break the for-await
          // loop sits idle through the save, blocking the input box for ~1–2s
          // after the user already saw the final token. Mirrors the explorer
          // pattern: detach from the stream as soon as the answer is final
          // and let the persistence finish in the background.
          if (event.completed === true) {
            break
          }
        }

        const finalAnswer: ChatAppResponse = {
          content: accumulated,
          context: lastContext,
          session_state: null,
          completed: true,
        }
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId ? { ...m, content: accumulated, answer: finalAnswer } : m
          )
        )
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        if (message !== "AbortError") {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsgId
                ? {
                    ...m,
                    content: t("tender.chat.streamErrorMessage"),
                  }
                : m
            )
          )
          toast.error(t("tender.chat.toastChatError"), { description: message })
        }
      } finally {
        setIsStreaming(false)
        setAbortStreaming(null)
        setActivityMessage("")
        abortControllerRef.current = null
      }
    },
    [
      isStreaming,
      currentSessionId,
      tenderCase,
      activeStep,
      extractionData,
      bidReviewData,
      cvMatchData,
      enableWebSearch,
      enableM365,
      hasM365Approval,
      enableHighReasoning,
      showM365Option,
      activeLibraryIds,
      setIsStreaming,
      setAbortStreaming,
      handleServerMessage,
      createSession,
      runId,
      t,
    ]
  )

  function startNewChat() {
    if (isStreaming) {
      handleCancel()
    }
    sessionCreatedLocally.current = false
    setCurrentSessionId(null)
    setCurrentSessionRunId(null)
    setMessages([])
  }

  function handleSelectSession(session: NonNullable<ChatSessionModel>) {
    if (session.id === currentSessionId) {
      setHistoryOpen(false)
      return
    }
    if (isStreaming) {
      handleCancel()
    }
    sessionCreatedLocally.current = false
    const id = session.id
    const sessionTenderRunId = (session as Record<string, unknown>).tenderRunId as
      | string
      | undefined
    setCurrentSessionRunId(sessionTenderRunId ?? null)
    setCurrentSessionId(id ?? null)
    setMessages([])
    if (id) {
      setIsHydrating(true)
    }
    setHistoryOpen(false)
  }

  function handleDeleteSession(sessionId: string) {
    deleteSession.mutate(
      { sessionId, caseId: tenderCase.id },
      {
        onSuccess: () => {
          if (currentSessionId === sessionId) {
            startNewChat()
          }
          toast.success(t("tender.chat.sessionDeletedToast"))
        },
      }
    )
  }

  function handleStartRename(session: NonNullable<ChatSessionModel>) {
    setRenamingSessionId(session.id ?? null)
    setRenameValue(session.title ?? "")
  }

  function handleConfirmRename() {
    if (!renamingSessionId || !renameValue.trim()) {
      setRenamingSessionId(null)
      return
    }
    renameSession.mutate(
      { sessionId: renamingSessionId, title: renameValue.trim(), caseId: tenderCase.id },
      {
        onSuccess: () => {
          setRenamingSessionId(null)
        },
      }
    )
  }

  const greetingAnswer = useMemo(
    () => makeAnswer(t("tender.chat.greeting", { title: tenderCase.title })),
    [t, tenderCase.title]
  )

  const handleM365Change = useCallback(
    (enabled: boolean) => {
      if (enabled && !hasM365Approval) {
        if (hasDeclinedM365) {
          toast.info(
            t("m365_tool.toast.declined_in_settings", {
              defaultValue: "Microsoft 365 access is off — turn it on in Preferences.",
            })
          )
          openPreferencesWith("general")
          return
        }
        setShowM365Info(true)
        return
      }
      setEnableM365(enabled)
    },
    [hasM365Approval, hasDeclinedM365, openPreferencesWith, t]
  )

  const handleM365Approve = useCallback(() => {
    approveM365()
    setEnableM365(true)
    setShowM365Info(false)
  }, [approveM365])

  const handleM365Decline = useCallback(() => {
    declineM365()
    setEnableM365(false)
    setShowM365Info(false)
  }, [declineM365])

  const handleM365Dismiss = useCallback(() => {
    setEnableM365(false)
    setShowM365Info(false)
  }, [])

  return (
    <CitationPreviewProvider>
      <div className="relative flex h-full flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-background/90 flex shrink-0 items-center gap-1.5 border-b px-4 py-3 backdrop-blur-sm">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className="truncate text-sm font-semibold">{t("tender.chat.panelTitle")}</span>
            <Badge
              className="bg-muted text-muted-foreground shrink-0 border-0 text-xs font-normal"
              variant="outline"
            >
              {t(STEP_BADGE_I18N_KEY[activeStep])}
            </Badge>
          </div>

          {/* New chat button */}
          <Button
            className="size-7"
            size="icon"
            title={t("tender.chat.newChatTooltip")}
            variant="ghost"
            onClick={startNewChat}
          >
            <Plus className="size-4" />
          </Button>

          {/* Session history popover */}
          <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
            <PopoverTrigger asChild>
              <Button
                className="size-7"
                size="icon"
                title={t("tender.chat.historyTooltip")}
                variant="ghost"
              >
                <Clock className="size-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 overflow-hidden p-0" sideOffset={6}>
              <div className="flex items-center justify-between px-3 py-2.5">
                <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                  {t("tender.chat.historyHeading")}
                </span>
                <Button
                  className="h-6 gap-1.5 px-2 text-xs"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    startNewChat()
                    setHistoryOpen(false)
                  }}
                >
                  <Plus className="size-3" />
                  {t("tender.chat.newChat")}
                </Button>
              </div>
              <Separator />
              <ScrollArea className="h-[min(16rem,_40dvh)]">
                {sessions.length === 0 ? (
                  <div className="p-3">
                    <p className="text-muted-foreground text-xs">{t("tender.chat.noSavedChats")}</p>
                  </div>
                ) : (
                  <div className="py-1">
                    {sessions.map((session) => {
                      const sessionId = session.id
                      if (!sessionId) return null
                      const isActive = currentSessionId === sessionId
                      const isRenaming = renamingSessionId === sessionId
                      const sessionRunId = (session as Record<string, unknown>).tenderRunId as
                        | string
                        | undefined
                      const isRunMismatch = !!sessionRunId && !!runId && sessionRunId !== runId

                      return (
                        <div
                          key={sessionId}
                          role="presentation"
                          className={cn(
                            "group flex items-center gap-2 px-3 py-2 transition-colors",
                            isActive ? "bg-accent" : "hover:bg-accent/50 cursor-pointer"
                          )}
                          onClick={
                            isRenaming
                              ? undefined
                              : () => {
                                  handleSelectSession(session)
                                }
                          }
                        >
                          {isRenaming ? (
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1">
                                <Input
                                  ref={renameInputRef}
                                  className="h-6 text-xs"
                                  value={renameValue}
                                  onChange={(e) => setRenameValue(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    e.stopPropagation()
                                    if (e.key === "Enter") handleConfirmRename()
                                    if (e.key === "Escape") setRenamingSessionId(null)
                                  }}
                                />
                                <Button
                                  className="-m-0.5 size-6 shrink-0 p-0"
                                  size="icon"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleConfirmRename()
                                  }}
                                >
                                  <Check className="size-3" />
                                </Button>
                              </div>
                            </div>
                          ) : (
                            // Mouse convenience on the row; keyboard users activate this button,
                            // whose click bubbles up to the row's onClick.
                            <button
                              className="focus-visible:ring-ring min-w-0 flex-1 rounded-sm text-left focus-visible:ring-1 focus-visible:outline-none"
                              type="button"
                            >
                              <span className="block truncate text-xs font-medium">
                                {session.title || t("tender.chat.defaultSessionTitle")}
                                {isRunMismatch && (
                                  <span
                                    className="text-muted-foreground ml-1 text-[10px]"
                                    title={t("tender.chat.otherRunTooltip")}
                                  >
                                    {t("tender.chat.otherRunBadge")}
                                  </span>
                                )}
                              </span>
                              <span className="text-muted-foreground block text-[10px]">
                                {formatSessionDate(session.updated)}
                              </span>
                            </button>
                          )}

                          {!isRenaming && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  className="focus-visible:ring-ring -m-0.5 size-6 shrink-0 p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:outline-none"
                                  size="icon"
                                  variant="ghost"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreHorizontal className="size-3" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="z-[100] w-36">
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleStartRename(session)
                                  }}
                                >
                                  <Pencil className="mr-2 size-3" />
                                  {t("tender.chat.renameSession")}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDeleteSession(sessionId)
                                  }}
                                >
                                  <Trash2 className="mr-2 size-3" />
                                  {t("tender.chat.deleteSession")}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </ScrollArea>
            </PopoverContent>
          </Popover>

          <Button
            className="-mr-1 size-7"
            size="icon"
            variant="ghost"
            onClick={() => useContentSidebarStore.getState().closePanel()}
          >
            <X className="size-4" />
          </Button>
        </div>

        {/* Activity indicator */}
        {isStreaming && activityMessage && (
          <div className="bg-muted/30 border-b px-4 py-1.5">
            <p className="text-muted-foreground truncate text-xs">{activityMessage}</p>
          </div>
        )}

        {/* Run mismatch banner */}
        {currentSessionRunId && runId && currentSessionRunId !== runId && (
          <div className="border-warning/30 bg-warning/[0.06] dark:border-warning flex items-center gap-2 border-b px-4 py-2">
            <p className="text-warning-foreground dark:text-warning-foreground text-xs">
              {t("tender.chat.runMismatchWarning")}
            </p>
            <Button className="ml-auto shrink-0" size="sm" variant="outline" onClick={startNewChat}>
              {t("tender.chat.newChat")}
            </Button>
          </div>
        )}

        {/* Messages */}
        <ScrollArea className="flex-1">
          <div
            ref={messagesContainerRef}
            className="flex flex-col gap-6 px-2 py-6"
            style={{ paddingBottom: "var(--chat-footer-height, 24px)" }}
          >
            {isHydrating && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="text-muted-foreground size-5 animate-spin" />
              </div>
            )}

            {showSuggestions && (
              <Chat.SystemMessage
                noCitation
                answer={greetingAnswer}
                citationSection={KNOWN_SESSION_TYPES.Navigator}
                isStreaming={false}
                showAnalysisPanelIcon={false}
                showCopyRaw={false}
                showDownload={false}
                showFactCheck={false}
              />
            )}

            {messages.map((msg, i) =>
              msg.role === "user" ? (
                <Chat.UserMessage key={msg.id} message={msg.content} />
              ) : (
                <Chat.SystemMessage
                  key={msg.id}
                  answer={msg.answer ?? makeAnswer(msg.content)}
                  citationSection={KNOWN_SESSION_TYPES.Navigator}
                  isLastMessage={i === messages.length - 1}
                  isStreaming={isStreaming && i === messages.length - 1}
                  noCitation={false}
                  showAnalysisPanelIcon={false}
                  showCopyRaw={false}
                  showDownload={false}
                  showFactCheck={false}
                  onCitationClick={handleCitationClick}
                />
              )
            )}

            {showSuggestions && (
              <div className="flex flex-wrap gap-2">
                {stepSuggestions.map((q) => (
                  <Button
                    key={q}
                    type="button"
                    variant="outline"
                    className={cn(
                      "text-muted-foreground hover:text-foreground hover:border-border/80 hover:bg-accent h-auto rounded-full px-3 py-1.5 text-left text-xs font-normal",
                      isStreaming && "pointer-events-none opacity-50"
                    )}
                    onClick={() => void handleSend(q)}
                  >
                    {q}
                  </Button>
                ))}
              </div>
            )}

            <div ref={bottomRef} />
          </div>
        </ScrollArea>

        {/* Footer */}
        <Chat.Footer
          clearOnSend
          hideVoiceInputButton
          hideVoiceMode
          changeTokenCounts={store.changeTokenCounts}
          disableSend={isStreaming || isHydrating}
          maxTokens={8000}
          placeholder={t("tender.chat.inputPlaceholder")}
          showCancelOption={isStreaming}
          showChatOptions={true}
          totalTokenCount={0}
          onCancel={handleCancel}
          onSend={(q) => void handleSend(q)}
          onSetQuestion={handleSetQuestion}
        >
          <ExplorerFooterToolbar
            hideVoiceMode
            enableAgent={false}
            enableHighReasoning={enableHighReasoning}
            enableM365={enableM365}
            enableNetbox={false}
            enableServiceDesk={false}
            enableWebSearch={enableWebSearch}
            hasAgentModeFeature={false}
            isPromptsFeatureEnabled={!!isPromptsFeatureEnabled}
            isStreaming={isStreaming}
            sessionType="tender"
            showHighReasoning={showHighReasoning}
            showM365Option={showM365Option}
            showNetboxOption={false}
            showServiceDeskOption={false}
            showWebSearchOption={showWebSearchOption}
            onAgentChange={() => {}}
            onHighReasoningChange={setEnableHighReasoning}
            onM365Change={handleM365Change}
            onNetboxChange={() => {}}
            onPromptClicked={(prompt) => setQuestionRef.current?.(prompt.content)}
            onServiceDeskChange={() => {}}
            onWebSearchChange={setEnableWebSearch}
          />
        </Chat.Footer>
      </div>

      <TenderCitationHoverPeek
        caseId={tenderCase.id}
        containerRef={messagesContainerRef}
        documents={documents}
        extractionLookup={extractionLookup}
        onOpenExtractionSource={openTenderSourcePreview}
      />
      <M365InfoModal
        open={showM365Info}
        onApprove={handleM365Approve}
        onDecline={handleM365Decline}
        onDismiss={handleM365Dismiss}
      />
    </CitationPreviewProvider>
  )
}
